/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Inject, Input, OnInit} from '@angular/core';
import * as _ from 'lodash';

import template from './retargeting-targeting.component.html';
import {ADN_TARGETING_TYPES} from '../targeting-constants';
import {TargetingHelper} from '../targeting-helper';
import {TargetingTemplateHook} from './targeting-template.component';
import {AdServerConfig} from '../../config/api-config';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';

interface RetargetingEntry {
  k: string;
  v: string;
  not: boolean;
}

interface RetargetingInputGroup {
  entries: RetargetingEntry[];
  notEntries: RetargetingEntry[];
}

@Component({
  standalone: false,
  selector: 'adn-retargeting-targeting',
  template
})
export class RetargetingTargetingComponent implements OnInit {
  @Input() model: any[];
  @Input() tabController: any;
  @Input() overridableTargeting: any;
  @Input() targetType: any = ADN_TARGETING_TYPES.retargeting;
  @Input() underModel: any;
  @Input() mode: string;
  @Input() isLockedDown: boolean;
  @Input() mandatory: boolean;

  loaded = true;
  inputs: RetargetingInputGroup[] = [];
  pref = {details: false};
  retargetingImages = '';

  readonly templateHook: TargetingTemplateHook = {
    getTemplateData: () => {
      this.prepareForSave();

      const mapEntries = _.map(_.get(this.model, [0, 'entries']), (val, key) => ({key, val}));
      const isSimple = this.model.length === 1 &&
        mapEntries.length === 1 &&
        mapEntries[0].val.length === 1 &&
        _.isEmpty(this.model[0].notEntries);

      return {
        vm: undefined,
        model: _.cloneDeep(this.model),
        constant: ADN_TARGETING_TYPES.retargeting,
        summary: isSimple
          ? (mapEntries[0].key + ': ' + mapEntries[0].val[0].join(', '))
          : TargetingHelper.getSizeOfTargetingTypes(this.model, ADN_TARGETING_TYPES.retargeting.targets) + ' retargeting with logic'
      };
    },
    copyTemplate: (tpl: any) => this.copyTemplate(tpl)
  };

  constructor(
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any
  ) {
  }

  ngOnInit(): void {
    this.init();

    if (this.tabController) {
      const prepareForSave = () => this.prepareForSave();
      const copyTemplate = (tpl: any) => this.copyTemplate(tpl);
      this.tabController.register(ADN_TARGETING_TYPES.retargeting.widget, () => undefined, copyTemplate);
      this.tabController.callBeforeSubmit(prepareForSave);
      this.tabController.registerAllTabs(prepareForSave);
    }

    const networkTag = this.LocalNetworkProfile.getNetworkTag();
    this.retargetingImages = '<img src="' + AdServerConfig.obtain().getUri('pixelr.gif') + '?network=' + networkTag + '&shop=petshop.com.au&expiry=2592000">\n' +
      '<img src="' + AdServerConfig.obtain().getUri('pixelr.gif') + '?network=' + networkTag + '&age=39&expiry=5184000">\n' +
      '<img src="' + AdServerConfig.obtain().getUri('pixelr.gif') + '?network=' + networkTag + '&sport=handball">';
  }

  addEntry(): void {
    this.inputs.push({entries: [{k: '', v: '', not: false}], notEntries: []});
  }

  addRow(index: number): void {
    this.inputs[index].entries.push({k: '', v: '', not: false});
  }

  addNotRow(index: number): void {
    this.inputs[index].notEntries.push({k: '', v: '', not: false});
  }

  negateRow(parentIndex: number, index: number, isNot?: boolean): void {
    const param = isNot ? 'notEntries' : 'entries';
    this.inputs[parentIndex][param][index].not = !this.inputs[parentIndex][param][index].not;
  }

  removeRow(parentIndex: number, index: number, isNot?: boolean): void {
    this.inputs[parentIndex][isNot ? 'notEntries' : 'entries'].splice(index, 1);
    if (this.inputs[parentIndex].entries.length === 0 && this.inputs[parentIndex].notEntries.length === 0) {
      this.inputs.splice(parentIndex, 1);
    }
  }

  private init(): void {
    this.inputs = [];

    _.forEach(this.model || [], (entryObj: any) => {
      const entries: RetargetingEntry[] = [];
      const notEntries: RetargetingEntry[] = [];
      this.setUpEntries(entryObj.entries, entries);
      this.setUpEntries(entryObj.notEntries, notEntries);
      this.inputs.push({entries, notEntries});
    });

    if (!this.inputs[0] || !this.inputs[0].entries || this.inputs[0].entries.length === 0) {
      this.inputs[0] = this.inputs[0] || {entries: [], notEntries: []};
      this.inputs[0].entries = this.inputs[0].entries || [];
      this.inputs[0].entries.push({k: '', v: '', not: false});
      this.inputs[0].notEntries = this.inputs[0].notEntries || [];
    }
  }

  private setUpEntries(data: any, savedData: RetargetingEntry[]): void {
    _.forEach(data, (v, k) => {
      const isNot = _.startsWith(String(k), 'NOT ');
      const nextLevelV = _.isArray(v[0]) ? v : [v];
      _.forEach(nextLevelV, (vLevel) => {
        savedData.push({
          k: isNot ? String(k).substr(4) : String(k),
          v: (vLevel || []).join(', '),
          not: isNot
        });
      });
    });
  }

  private prepareForSave(): void {
    this.model = this.model || [];
    this.model.length = 0;

    _.forEach(this.inputs, (theInput) => {
      const formattedForSave = {};
      const notFormattedForSave = {};
      let hasAnUpdate = false;

      const loopThrough = (param: 'entries' | 'notEntries', objForSave: any) => {
        _.forEach(theInput[param], (entry) => {
          const trimmedKeyOnly = _.trim(entry.k);
          const trimKey = (entry.not ? 'NOT ' : '') + trimmedKeyOnly;
          const trimValues = _.trim(entry.v);

          if (_.isString(trimmedKeyOnly) && trimmedKeyOnly.length > 0 && _.isString(trimValues) && trimValues.length > 0) {
            const values = trimValues.split(',');
            const mappedValues = _.filter(_.map(values, (v) => _.trim(v)), (v) => _.isString(v) && v.length > 0);
            objForSave[trimKey] = objForSave[trimKey] || [];
            objForSave[trimKey].push(mappedValues);
            hasAnUpdate = true;
          }
        });
      };

      loopThrough('entries', formattedForSave);
      loopThrough('notEntries', notFormattedForSave);

      if (hasAnUpdate) {
        this.model.push({entries: formattedForSave, notEntries: notFormattedForSave});
      }
    });

    this.init();
  }

  private copyTemplate(tpl: any): void {
    const copied = _.cloneDeep(_.get(tpl, ['data', 'model'])) || [];
    this.model = this.model || [];
    this.model.length = 0;
    Array.prototype.push.apply(this.model, copied);
    this.init();
  }
}
